import type { StateCreator } from 'zustand';
import { logError } from 'setup/logger/logError';
import { issueClient } from 'shared/domain/issue/issueClient';
import type { IssuesQuery } from 'shared/domain/issue/issueQuery';
import { classifyEntityEvent } from 'store/entityCollection/model';
import { EntityEventReaction } from 'store/entityCollection/types';
import { createLatestRead } from 'store/latestRead';
import { IssuesSlice } from './types';

/** Changes often come in bursts (a sync, several edits): read the page once they settle. */
const REFRESH_AFTER_CHANGES_DELAY_MS = 500;

const initialState: Pick<IssuesSlice, 'issues' | 'loading' | 'requestFailed'> = {
  issues: { items: [], total: 0 },
  loading: true,
  requestFailed: false,
};

export const createIssuesSlice: StateCreator<IssuesSlice> = (set) => {
  const latestRead = createLatestRead();
  let shownQuery: IssuesQuery | undefined;
  let pendingRefresh: ReturnType<typeof setTimeout> | undefined;

  const cancelPendingRefresh = (): void => {
    clearTimeout(pendingRefresh);
    pendingRefresh = undefined;
  };

  /** A new query shows the spinner; a refresh after changes keeps the shown page until the new one comes. */
  function readIssuesPage(query: IssuesQuery, isRefresh: boolean): Promise<void> {
    shownQuery = query;
    if (!isRefresh) {
      set({ loading: true, requestFailed: false });
    }
    return latestRead.run(() => issueClient.getFiltered(query), {
      onResult: (issues) => set({ issues, requestFailed: false, loading: false }),
      onError: (error) => {
        logError('Error while reading issues', error);
        if (!isRefresh) {
          set({ requestFailed: true, loading: false });
        }
      },
    });
  }

  return {
    ...initialState,
    loadIssuesPage: (query) => readIssuesPage(query, false),
    subscribeToIssueChanges: () => {
      const stopEvents = issueClient.onEvent((event) => {
        const { reaction } = classifyEntityEvent(event, issueClient.readCollection);
        const isChange = reaction === EntityEventReaction.reload || reaction === EntityEventReaction.applyCollection;
        if (!isChange || !shownQuery) {
          return;
        }
        cancelPendingRefresh();
        pendingRefresh = setTimeout(() => {
          pendingRefresh = undefined;
          if (shownQuery) {
            void readIssuesPage(shownQuery, true);
          }
        }, REFRESH_AFTER_CHANGES_DELAY_MS);
      });
      return (): void => {
        stopEvents();
        cancelPendingRefresh();
      };
    },
    resetIssues: () => {
      latestRead.cancel();
      shownQuery = undefined;
      cancelPendingRefresh();
      set(initialState);
    },
  };
};

export type { IssuesSlice } from './types';
